import { routingSwitchingWanFlags } from '@ipfabric/utils/schema/siteSeparation/routingSwitchingSiteSeparationRule';
import type { SiteSeparationRule } from '@ipfabric/utils/schema/siteSeparation/siteSeparationRule';
import { CloudVendors } from '@ipfabric/utils/schema/siteSeparation/slugSiteSeparationRule';
import { generateGuid } from 'utils/guid';
import type {
  RuleEditorValues,
  SiteSeparationRuleType,
} from './RuleEditor.types';
import type {
  TestRuleValues,
  TestableRuleType,
} from './TestRuleSidePanelContent.helpers';

export const RULE_TYPE_OPTIONS: Array<{
  label: string;
  value: SiteSeparationRuleType;
}> = [
  { label: 'Hostname (On-premise network)', value: 'regexHostname' },
  { label: 'SNMP location (On-premise network)', value: 'regexSnmpLocation' },
  { label: 'Routing & switching', value: 'routingSwitching' },
  { label: 'Cloud resource ID (Cloud network)', value: 'regexCloudResourceId' },
  { label: 'Cloud Tags', value: 'cloudResourceTag' },
  { label: 'Slug (Cloud network)', value: 'slug' },
];

const BASE_EDITOR_VALUES: Omit<RuleEditorValues, 'type'> = {
  note: '',
  regex: '',
  siteName: '',
  transformation: 'none',
  applyToCloudInstances: false,
  applyToAllVendors: false,
  minimumSiteSize: '1',
  firewall: false,
  wan: [...routingSwitchingWanFlags],
  enabledVendors: [],
  tagNames: [],
};

export const isTestRuleAvailable = (
  type: SiteSeparationRuleType,
): type is TestableRuleType => type !== 'routingSwitching' && type !== 'slug';

export const getTestRuleValuesFromEditor = (
  values: RuleEditorValues,
): TestRuleValues | null => {
  if (!isTestRuleAvailable(values.type)) {
    return null;
  }

  if (values.type === 'cloudResourceTag') {
    return {
      type: values.type,
      tagNames: values.tagNames,
      transformation: values.transformation,
      applyToCloudInstances: values.applyToCloudInstances,
    };
  }

  return {
    type: values.type,
    regex: values.regex,
    siteName: values.siteName,
    transformation: values.transformation,
    applyToCloudInstances: values.applyToCloudInstances,
  };
};

export const getEditorDefaultValues = (
  rule?: SiteSeparationRule,
): RuleEditorValues => {
  if (!rule) {
    return { ...BASE_EDITOR_VALUES, type: 'regexHostname' };
  }

  switch (rule.type) {
    case 'regexHostname':
    case 'regexSnmpLocation':
      return {
        ...BASE_EDITOR_VALUES,
        type: rule.type,
        note: rule.note,
        regex: rule.regex,
        siteName: rule.siteName,
        transformation: rule.transformation,
      };
    case 'regexCloudResourceId':
      return {
        ...BASE_EDITOR_VALUES,
        type: rule.type,
        note: rule.note,
        regex: rule.regex,
        siteName: rule.siteName,
        transformation: rule.transformation,
        applyToCloudInstances:
          'applyToCloudInstances' in rule ? rule.applyToCloudInstances : false,
      };
    case 'routingSwitching':
      return {
        ...BASE_EDITOR_VALUES,
        type: rule.type,
        note: rule.note,
        transformation: rule.transformation,
        minimumSiteSize: String(rule.minimumSiteSize),
        firewall: rule.firewall,
        wan: rule.wan,
      };
    case 'cloudResourceTag':
      return {
        ...BASE_EDITOR_VALUES,
        type: rule.type,
        note: rule.note,
        transformation: rule.transformation,
        tagNames: rule.tagNames,
        applyToCloudInstances: rule.applyToCloudInstances,
      };
    case 'slug':
      return {
        ...BASE_EDITOR_VALUES,
        type: rule.type,
        note: rule.note,
        transformation: rule.transformation,
        applyToCloudInstances: rule.applyToCloudInstances,
        applyToAllVendors: rule.enabledVendors.length === CloudVendors.length,
        enabledVendors: rule.enabledVendors,
      };
  }
};

export const createRuleFromEditorValues = (
  values: RuleEditorValues,
  existingRuleId?: string,
): SiteSeparationRule => {
  const id = existingRuleId ?? generateGuid();
  switch (values.type) {
    case 'regexHostname':
    case 'regexSnmpLocation':
      return {
        id,
        type: values.type,
        note: values.note,
        regex: values.regex,
        siteName: values.siteName,
        transformation: values.transformation,
      };
    case 'regexCloudResourceId':
      return {
        id,
        type: values.type,
        note: values.note,
        regex: values.regex,
        siteName: values.siteName,
        transformation: values.transformation,
        applyToCloudInstances: values.applyToCloudInstances,
      };
    case 'routingSwitching':
      return {
        id,
        type: values.type,
        note: values.note,
        transformation: values.transformation,
        minimumSiteSize: Number.parseInt(values.minimumSiteSize, 10) || 1,
        firewall: values.firewall,
        wan: values.wan,
      };
    case 'cloudResourceTag':
      return {
        id,
        type: values.type,
        note: values.note,
        transformation: values.transformation,
        tagNames: values.tagNames,
        applyToCloudInstances: values.applyToCloudInstances,
      };
    case 'slug':
      return {
        id,
        type: values.type,
        note: values.note,
        transformation: values.transformation,
        enabledVendors: values.enabledVendors,
        applyToCloudInstances: values.applyToCloudInstances,
      };
  }
};
